import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import Popover from '@amzn/awsui-components-console/popover';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import { TranslationProgress, TranslationStatus } from '../types';

interface SubmitCancelActionBarProps {
  hasPendingChanges: boolean;
  translationStatus: TranslationStatus;
  translationProgress: TranslationProgress;
  submitLoading: boolean;
  /**
   * When false, Submit and Cancel are disabled: either another client owns the group
   * or the caller lacks SOURCE_MANAGEMENT. Use {@link isEditableClient} to tell which.
   */
  canEdit?: boolean;

  /**
   * The ownership half of `canEdit`, separate because the disabled-Cancel copy names a
   * reason and `canEdit` alone cannot distinguish ownership from a missing permission.
   */
  isEditableClient?: boolean;

  /**
   * Whether the permission answer is still in flight. Both controls show a skeleton until it
   * lands: with a translation in progress, Cancel would otherwise be clickable before the
   * permission is known and the click would 403. Reason popovers are suppressed too, since no
   * reason is established yet.
   */
  permissionsLoading?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

const SubmitCancelActionBar: React.FC<SubmitCancelActionBarProps> = ({
  hasPendingChanges,
  translationStatus,
  translationProgress,
  submitLoading,
  canEdit = true,
  isEditableClient = true,
  permissionsLoading = false,
  onSubmit,
  onCancel,
}) => {
  const isInProgress = translationStatus === TranslationStatus.InProgress;
  const isCancelled = translationStatus === TranslationStatus.Cancelled;

  // Submit disabled when: read-only group OR no pending changes OR currently
  // loading OR a translation is already in progress (resubmission is blocked
  // while in flight, matching the "cannot be resubmitted" warning below).
  // `!canEdit` only counts once the answer is known.
  const permissionDenied = !permissionsLoading && !canEdit;

  const submitDisabled =
    permissionDenied || !hasPendingChanges || submitLoading || isInProgress;

  // Cancel disabled when: read-only group OR translation is not in progress
  const cancelDisabled = permissionDenied || !isInProgress;

  // Copy for the disabled Cancel, naming the reason that actually applies.
  // `undefined` when the only reason is a missing permission: that gets no
  // explanatory copy, matching the rest of the gated controls.
  const cancelDisabledReason = !isEditableClient
    ? 'This group is owned by another client and is read-only in LangHub.'
    : !isInProgress
      ? 'Translation is not in progress'
      : undefined;

  const disabledCancelButton = (
    <Button disabled>
      <Box color="text-status-inactive" fontWeight="bold">
        Cancel Translation
      </Box>
    </Button>
  );

  // Cancellation context calculations — include all locales for accurate percentage
  const completedCount = translationProgress.completedLocales.length;
  const totalCount =
    translationProgress.completedLocales.length +
    translationProgress.inProgressLocales.length +
    translationProgress.failedLocales.length +
    translationProgress.cancelledLocales.length;
  const progress =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <Container>
      <SpaceBetween size="m">
        <SpaceBetween direction="horizontal" size="xs">
          {permissionsLoading ? (
            <>
              <Skeleton display="inline-block" height="32px" width="230px" />
              <Skeleton display="inline-block" height="32px" width="150px" />
            </>
          ) : (
            <>
              <Popover
                content="Sends all strings in this group to TRANSCOM for translation into target locales."
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button
                  variant="primary"
                  disabled={submitDisabled}
                  loading={submitLoading}
                  onClick={onSubmit}
                  iconName="status-info"
                >
                  Submit group for translation
                </Button>
              </Popover>
              {cancelDisabled ? (
                cancelDisabledReason ? (
                  <Popover
                    content={cancelDisabledReason}
                    dismissButton={false}
                    position="top"
                    size="small"
                    triggerType="custom"
                  >
                    {disabledCancelButton}
                  </Popover>
                ) : (
                  disabledCancelButton
                )
              ) : (
                <Button onClick={onCancel}>
                  <Box color="text-status-error" fontWeight="bold">
                    Cancel Translation
                  </Box>
                </Button>
              )}
            </>
          )}
        </SpaceBetween>

        {isInProgress && (
          <Alert type="warning">
            Group is under translation and cannot be resubmitted. Cancel the
            current job and resubmit only if urgent changes are needed.
          </Alert>
        )}

        {isCancelled && totalCount > 0 && (
          <Alert type="warning">
            This translation job was cancelled at {progress}% completion (
            {completedCount} of {totalCount} locales completed). Some locales
            did not receive translations. Roll back to the last fully-translated
            version from Version History if needed, or resubmit this group for
            translation.
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default SubmitCancelActionBar;
